<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { userStatsApi, type ChartType, type StatsListItem, type StatsPoint, type StatsRange } from '@/services/api/userStats';

const range = ref<StatsRange>('WeeksInMonth');
const chartType = ref<ChartType>('oar');
const points = ref<StatsPoint[]>([]);
const loading = ref(false);

const drillDown = ref<StatsListItem[] | null>(null);
const drillLoading = ref(false);

async function reload(): Promise<void> {
  drillDown.value = null;
  loading.value = true;
  try {
    points.value = await userStatsApi.get(range.value, chartType.value);
  } finally {
    loading.value = false;
  }
}

async function setRange(r: StatsRange): Promise<void> {
  range.value = r;
  await reload();
}

async function setChartType(c: ChartType): Promise<void> {
  chartType.value = c;
  await reload();
}

async function showDrillDown(p: StatsPoint): Promise<void> {
  if (chartType.value !== 'users') return;
  const partOfPeriod = range.value === 'WeeksInMonth' ? p.period : p.year;
  drillLoading.value = true;
  try {
    drillDown.value = await userStatsApi.getList(range.value, partOfPeriod, chartType.value);
  } finally {
    drillLoading.value = false;
  }
}

onMounted(reload);
</script>

<template>
  <section class="userstats">
    <h1>User Stats</h1>
    <div class="userstats__controls">
      <fieldset>
        <legend>Range</legend>
        <button type="button" :aria-pressed="range === 'WeeksInMonth'" @click="setRange('WeeksInMonth')">Weeks</button>
        <button type="button" :aria-pressed="range === 'Month'" @click="setRange('Month')">Months</button>
        <button type="button" :aria-pressed="range === 'Quarter'" @click="setRange('Quarter')">Quarters</button>
        <button type="button" :aria-pressed="range === 'Year'" @click="setRange('Year')">Years</button>
      </fieldset>
      <fieldset>
        <legend>Data</legend>
        <button type="button" :aria-pressed="chartType === 'oar'" @click="setChartType('oar')">Orders / Reservations</button>
        <button type="button" :aria-pressed="chartType === 'users'" @click="setChartType('users')">Users Created</button>
      </fieldset>
    </div>

    <p v-if="loading">Loading…</p>
    <template v-else>
      <table class="userstats__table">
        <thead>
          <tr>
            <th>Period</th>
            <th>Year</th>
            <th v-if="chartType === 'oar'">Orders</th>
            <th v-if="chartType === 'oar'">Reservations</th>
            <th v-if="chartType === 'users'">Users Created</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(p, i) in points"
            :key="i"
            :class="{ 'userstats__row--clickable': chartType === 'users' }"
            @click="showDrillDown(p)"
          >
            <td>{{ p.period }}</td>
            <td>{{ p.year }}</td>
            <td v-if="chartType === 'oar'">{{ p.orders ?? '—' }}</td>
            <td v-if="chartType === 'oar'">{{ p.reservations ?? '—' }}</td>
            <td v-if="chartType === 'users'">{{ p.numberOfUsersCreated ?? '—' }}</td>
          </tr>
        </tbody>
      </table>

      <template v-if="chartType === 'users'">
        <p v-if="drillLoading">Loading users…</p>
        <template v-else-if="drillDown !== null">
          <h2>Users in Period</h2>
          <button type="button" class="userstats__close" @click="drillDown = null">✕ Close</button>
          <table class="userstats__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>EMSL User ID</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in drillDown" :key="u.email">
                <td>{{ u.name }}</td>
                <td>{{ u.email }}</td>
                <td>{{ u.emsl_user_id ?? '—' }}</td>
                <td>{{ u.created_at ? new Date(u.created_at).toLocaleDateString() : '—' }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="drillDown.length === 0">No users in this period.</p>
        </template>
        <p v-else class="userstats__hint">Click a row to see individual users.</p>
      </template>
    </template>
  </section>
</template>

<style scoped>
.userstats {
  max-width: 60rem;
  margin: 0 auto;
}
.userstats__controls {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}
.userstats__controls fieldset {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
}
.userstats__controls button {
  margin: 0 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  cursor: pointer;
}
.userstats__controls button[aria-pressed='true'] {
  background: #2563eb;
  color: #fff;
}
.userstats__table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}
.userstats__table th,
.userstats__table td {
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}
.userstats__row--clickable {
  cursor: pointer;
}
.userstats__row--clickable:hover {
  background: #f9fafb;
}
.userstats__hint {
  color: #6b7280;
  font-size: 0.875rem;
}
.userstats__close {
  margin-bottom: 0.75rem;
  padding: 0.25rem 0.75rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  cursor: pointer;
}
h2 {
  margin: 1.5rem 0 0.5rem;
  font-size: 1.125rem;
}
</style>
